import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container",
    "nameInput",
    "emailInput",
    "phoneInput",
    "companyInput",
    "titleInput",
    "notesInput",
    "duplicateWarning",
    "duplicateList",
    "errorMessage",
    "errorText",
    "submitButton",
    "submitText",
    "spinner"
  ]
  
  static values = {
    searchUrl: String,
    createUrl: String,
    triggerId: String
  }
  
  connect() {
    this.debounceTimer = null
    
    // Bind to external trigger button if specified
    if (this.hasTriggerIdValue && this.triggerIdValue) {
      const trigger = document.getElementById(this.triggerIdValue)
      if (trigger) {
        this.boundOpen = this.open.bind(this)
        trigger.addEventListener("click", this.boundOpen)
      }
    }
  }
  
  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    // Remove event listener from trigger
    if (this.boundOpen && this.hasTriggerIdValue) {
      const trigger = document.getElementById(this.triggerIdValue)
      if (trigger) {
        trigger.removeEventListener("click", this.boundOpen)
      }
    }
  }
  
  open() {
    this.element.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    // Focus on name input
    setTimeout(() => {
      if (this.hasNameInputTarget) {
        this.nameInputTarget.focus()
      }
    }, 100)
  }
  
  close() {
    this.element.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.resetForm()
  }
  
  resetForm() {
    if (this.hasNameInputTarget) this.nameInputTarget.value = ""
    if (this.hasEmailInputTarget) this.emailInputTarget.value = ""
    if (this.hasPhoneInputTarget) this.phoneInputTarget.value = ""
    if (this.hasCompanyInputTarget) this.companyInputTarget.value = ""
    if (this.hasTitleInputTarget) this.titleInputTarget.value = ""
    if (this.hasNotesInputTarget) this.notesInputTarget.value = ""
    this.hideDuplicateWarning()
    this.hideError()
  }
  
  checkDuplicates() {
    // Debounce the search
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    
    this.debounceTimer = setTimeout(() => {
      this.performDuplicateCheck()
    }, 300)
  }
  
  async performDuplicateCheck() {
    const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
    const email = this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : ""
    const company = this.hasCompanyInputTarget ? this.companyInputTarget.value.trim() : ""
    
    // Need at least a name with 2+ characters to search
    if (name.length < 2 && email.length < 3) {
      this.hideDuplicateWarning()
      return
    }
    
    // Search for duplicates
    const searchQuery = name || email
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          "Accept": "application/json"
        }
      })
      
      if (!response.ok) return
      
      const people = await response.json()
      
      // Filter for potential duplicates
      const duplicates = people.filter(person => {
        const nameMatch = name && person.name && 
          person.name.toLowerCase().includes(name.toLowerCase())
        const emailMatch = email && person.email && 
          person.email.toLowerCase() === email.toLowerCase()
        const companyMatch = company && person.company && 
          person.company.toLowerCase().includes(company.toLowerCase()) && nameMatch
        
        return nameMatch || emailMatch || companyMatch
      })
      
      if (duplicates.length > 0) {
        this.showDuplicateWarning(duplicates)
      } else {
        this.hideDuplicateWarning()
      }
    } catch (error) {
      console.error("Error checking for duplicates:", error)
    }
  }
  
  showDuplicateWarning(duplicates) {
    if (!this.hasDuplicateWarningTarget || !this.hasDuplicateListTarget) return
    
    const html = duplicates.slice(0, 3).map(person => {
      const details = [person.company, person.email].filter(Boolean).join(" • ")
      return `
        <a href="/people/${person.id}" 
           class="block p-2 mt-1 rounded hover:bg-amber-100 transition-colors"
           target="_blank">
          <span class="font-medium">${this.escapeHtml(person.name)}</span>
          ${details ? `<span class="text-amber-600"> — ${this.escapeHtml(details)}</span>` : ''}
        </a>
      `
    }).join('')
    
    this.duplicateListTarget.innerHTML = html
    this.duplicateWarningTarget.classList.remove("hidden")
  }
  
  hideDuplicateWarning() {
    if (this.hasDuplicateWarningTarget) {
      this.duplicateWarningTarget.classList.add("hidden")
    }
  }
  
  showError(message) {
    if (this.hasErrorMessageTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }
  
  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }
  
  async submit(event) {
    event.preventDefault()
    
    const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
    
    if (!name) {
      this.showError("Name is required")
      return
    }
    
    // Show loading state
    this.setLoading(true)
    this.hideError()
    
    // Build form data
    const formData = {
      person: {
        name: name,
        email: this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : "",
        phone: this.hasPhoneInputTarget ? this.phoneInputTarget.value.trim() : "",
        company: this.hasCompanyInputTarget ? this.companyInputTarget.value.trim() : "",
        title: this.hasTitleInputTarget ? this.titleInputTarget.value.trim() : "",
        notes: this.hasNotesInputTarget ? this.notesInputTarget.value.trim() : ""
      }
    }
    
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify(formData)
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Redirect to the new person's page
        window.location.href = data.person.url
      } else {
        this.showError(data.errors ? data.errors.join(", ") : "Failed to create person")
        this.setLoading(false)
      }
    } catch (error) {
      console.error("Error creating person:", error)
      this.showError("An error occurred. Please try again.")
      this.setLoading(false)
    }
  }
  
  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.textContent = loading ? "Adding..." : "Add Person"
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

